import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  buildRegisterWithInviteUrl,
  redirectToRegisterWithInvite,
} from "@/hooks/usePostRegisterInvite";
import { isMaintenanceMode } from "@/utils/maintenanceMode";

const API = `${process.env.REACT_APP_API_URL}/api`;
const APP_BASE_URL = process.env.REACT_APP_APP_URL || "";

function formatBenefit(benefit) {
  if (!benefit) return [];
  const rows = [];
  if (benefit.discount_type === "percent_off" && benefit.discount_value) {
    rows.push(`Diskon ${benefit.discount_value}%`);
  }
  if (benefit.discount_type === "amount_off" && benefit.discount_value) {
    rows.push(`Diskon Rp ${Number(benefit.discount_value).toLocaleString("id-ID")}`);
  }
  if (benefit.trial_plan && benefit.trial_days) {
    rows.push(`Akses ${String(benefit.trial_plan).toUpperCase()} gratis ${benefit.trial_days} hari`);
  }
  if (benefit.bonus_credits) {
    rows.push(`Bonus ${benefit.bonus_credits} kredit`);
  }
  if (!rows.length) {
    rows.push("Benefit campaign tersedia sesuai konfigurasi admin.");
  }
  return rows;
}

export default function InvitePage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const maintenanceMode = isMaintenanceMode();

  useEffect(() => {
    if (!maintenanceMode) return;
    navigate("/", { replace: true });
  }, [maintenanceMode, navigate]);

  useEffect(() => {
    if (maintenanceMode) {
      setLoading(false);
      return () => {};
    }

    let mounted = true;
    const run = async () => {
      setLoading(true);
      setMessage("");
      try {
        const res = await axios.get(`${API}/invite/${encodeURIComponent(code)}/preview`);
        if (!mounted) return;
        setPreview(res.data || null);
      } catch (err) {
        if (!mounted) return;
        setPreview(null);
        setMessage(err?.response?.data?.detail || "Kode invite tidak valid atau sudah tidak aktif.");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    if (code) run();
    return () => {
      mounted = false;
    };
  }, [code, maintenanceMode]);

  const benefitRows = useMemo(() => formatBenefit(preview?.benefit), [preview?.benefit]);
  const registerUrl = buildRegisterWithInviteUrl(code);

  if (maintenanceMode) return null;

  return (
    <main className="max-w-3xl mx-auto px-4 md:px-8 py-10">
      <section className="border border-border rounded-md bg-card/60 p-6 md:p-8 space-y-5">
        <h1 className="font-heading text-3xl font-bold tracking-tight">Aktivasi Invite</h1>

        {loading && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Memuat preview benefit...</span>
          </div>
        )}

        {!loading && !preview && (
          <div className="space-y-4">
            <p className="text-sm text-red-300">{message || "Kode tidak dapat digunakan."}</p>
            <div className="flex flex-wrap items-center gap-3">
              <Button
                onClick={() => redirectToRegisterWithInvite(code)}
                className="rounded-sm bg-brand-primary hover:bg-brand-primary/90 text-white"
              >
                Daftar dengan kode ini
              </Button>
              <Link to="/pricing" className="text-sm text-brand-primary hover:underline">
                Lihat paket dan harga
              </Link>
            </div>
            <p className="text-xs text-muted-foreground">
              Link langsung:{" "}
              <a href={registerUrl} className="text-brand-primary hover:underline">
                {registerUrl}
              </a>
            </p>
          </div>
        )}

        {!loading && preview && (
          <>
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Campaign: <span className="text-foreground font-medium">{preview.campaign?.name || "-"}</span>
              </p>
              {preview.target_name && (
                <p className="text-sm text-muted-foreground">
                  Undangan untuk: <span className="text-foreground font-medium">{preview.target_name}</span>
                </p>
              )}
              {preview.expires_at && (
                <p className="text-sm text-muted-foreground">
                  Berlaku sampai:{" "}
                  <span className="text-foreground font-medium">
                    {new Date(preview.expires_at).toLocaleString("id-ID")}
                  </span>
                </p>
              )}
            </div>

            <div className="rounded-md border border-border bg-background/50 p-4">
              <p className="text-sm font-medium mb-2">Benefit yang Anda dapatkan:</p>
              <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                {benefitRows.map((row) => (
                  <li key={row}>{row}</li>
                ))}
              </ul>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Button
                onClick={() => redirectToRegisterWithInvite(code)}
                className="rounded-sm bg-brand-primary hover:bg-brand-primary/90 text-white"
              >
                Daftar dengan kode ini
              </Button>
              <a href={`${APP_BASE_URL}/login`} className="text-sm text-brand-primary hover:underline">
                Sudah punya akun? Login
              </a>
            </div>
            <p className="text-xs text-muted-foreground">
              Jika tombol tidak bekerja, buka langsung:{" "}
              <a href={registerUrl} className="text-brand-primary hover:underline">
                {registerUrl}
              </a>
            </p>
          </>
        )}
      </section>
    </main>
  );
}
